import React from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import useAuth from '../../hooks/use-auth';
import Button from '../elements/Button';
import { Input } from '../elements/Input';
import { H2, P, PExtraSmall } from '../elements/Text';
import * as CrashReporter from '../../crash-reporter';
import Turnstile, { TurnstileHandle } from './Turnstile';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  margin: 0 auto;
  text-align: center;
  max-width: ${({ theme }) => theme.dimensions.readableColumnWidth};

  h1 {
    margin-top: 0;
  }
`;

const ContentsWrapper = styled.div`
  display: flex;
  max-width: 400px;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  flex-direction: column;

  button {
    flex: 1;
    width: 100%;
  }
`;

const TextButton = styled(PExtraSmall)<{ isVisible: boolean }>`
  cursor: pointer;
  text-decoration: underline;
  margin-top: 16px;
  font-weight: 400;
  transition: all 0.3s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ isVisible }) =>
    !isVisible &&
    css`
      pointer-events: none;
      opacity: 0;
      cursor: default;
    `}
`;

export default function ConfirmEmail({
  email,
  redirectUri,
  returnTo,
}: {
  email: string;
  redirectUri: string;
  returnTo?: string;
}) {
  const { passwordlessLogin, passwordlessStart } = useAuth();
  const [passwordlessCode, setPasswordlessCode] = React.useState('');
  const [shouldShowRequestNewCode, setShouldShowRequestNewCode] =
    React.useState(false);
  const turnstileRef = React.useRef<TurnstileHandle>(null);
  const submitCodeMutation = useMutation({
    mutationFn: async () => {
      try {
        const { isAppUserOnly } = await passwordlessLogin({
          email,
          code: passwordlessCode,
        });
        if (isAppUserOnly) {
          return;
        }
        // full navigation so the app boots from the freshly stored session,
        // matching the old redirect-based flow. returnTo is the final
        // destination; falling back to the auth-callback redirectUri keeps
        // flows with their own callback handling (solo onboarding) working
        window.location.assign(returnTo ?? redirectUri);
      } catch (error) {
        setShouldShowRequestNewCode(true);
        const status = (error as { response?: { status?: number } })?.response
          ?.status;

        if (status === 401) {
          toast.error('Sorry, this code is not valid.');
        } else if (status === 429) {
          toast.error('Too many attempts. Please request a new code.');
        } else {
          toast.error('Sorry, something went wrong.');
          CrashReporter.reportError(error as Error);
        }
      }
    },
  });

  const requestNewCodeMutation = useMutation({
    mutationFn: async () => {
      // the challenge runs on click and only appears if Cloudflare asks for
      // it; solving it continues straight through to resending the code
      const turnstile = turnstileRef.current;
      if (!turnstile) {
        toast.error('Sorry, something went wrong.');
        return;
      }

      let turnstileToken: string;
      try {
        turnstileToken = await turnstile.execute();
      } catch {
        toast.error("We couldn't verify your browser. Please try again.");
        return;
      }

      try {
        await passwordlessStart({
          email,
          turnstileToken,
        });
        toast.success('A new code has been sent to your email.');
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 400) {
          toast.error('Sorry, that email is not valid.');
        } else if (status === 403) {
          toast.error(
            "We couldn't verify your browser. Please try again in a moment."
          );
        } else {
          toast.error('Sorry, something went wrong.');
          CrashReporter.reportError(error as Error);
        }
      }
    },
  });

  return (
    <Wrapper>
      <H2>Check your email for a code</H2>
      <P>
        We sent a code to <b>{email}</b>. The code expires shortly, so please
        enter it soon.
      </P>
      <ContentsWrapper>
        <Input
          autoComplete="one-time-code"
          type="text"
          placeholder="Code"
          value={passwordlessCode}
          onChange={(e) => setPasswordlessCode(e.target.value)}
        />
        <Button
          label="Continue"
          type="primary"
          onClick={submitCodeMutation.mutate}
          isLoading={submitCodeMutation.isLoading}
        />
        {/* Turnstile protects resending a code (which sends email), not
            submitting one. The link is always clickable once offered; the
            challenge appears below it only if Cloudflare asks for one. */}
        <TextButton
          onClick={() => requestNewCodeMutation.mutate()}
          isVisible={shouldShowRequestNewCode}
        >
          Request a new code
        </TextButton>
        <Turnstile ref={turnstileRef} />
      </ContentsWrapper>
    </Wrapper>
  );
}
